import { useEffect, useState, useContext, useRef } from "react";
import { createBrowserRouter, RouterProvider, useNavigate, useParams } from "react-router";
import axios from 'axios';

import { AppContext } from '../../../context.js';

import Helmet from "../../components/helmet.js";
import { Status, Button, Counter } from '../../components/component.js';

const CARECIERGE_BOT_ID = "000000000000000000000001";

function User() {
  const context = useContext(AppContext);
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [user, setUser] = useState(null);
  
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [speciality, setSpeciality] = useState("");

  const [phone, setPhone] = useState("");
  
  const [provider, setProvider] = useState(false);
  const [npi, setNpi] = useState("");

  const [email, setEmail] = useState("");

  const [disableConfirm, setDisableConfirm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [attachments, setAttachments] = useState(null);

  const isProtected = id === CARECIERGE_BOT_ID || context.id === id;

  useEffect(() => {
    let u = context.addSpiner();
    axios.post('/api/admin/physicians', {
      filter: {
        id: [id]
      }
    }).then((response) => {
      const userData = response.data.physicians[0];
      setUser(userData);
      setFirstName(userData.firstName || "");
      setMiddleName(userData.middleName || "");
      setLastName(userData.lastName || "");
      setSpeciality(userData.speciality || "");
      setPhone(userData.phone || "");
      setProvider(userData.provider || false);
      setNpi(userData.npi || "");
      setEmail(userData.email || "");
      setLoading(false);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }, [id]);

  function updateUser() {
    let u = context.addSpiner();
    axios.post('/api/admin/physicians/update', {
      id,
      firstName: firstName ? firstName : null,
      middleName: middleName ? middleName : null,
      lastName: lastName ? lastName : null,
      speciality: speciality ? speciality : null,
      phone: phone ? phone : null,
      provider: provider,
      npi: npi ? npi : null,
      email: email ? email : null,
    }).then((response) => {
      navigate('/settings/users');
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function disableUser() {
    let u = context.addSpiner();
    axios.post('/api/admin/physicians/updateStatus', {
      id,
      statusType: "ARCHIVED",
    }).then((response) => {
      setUser({
        ...user,
        statusType: response.data.statusType,
        archivedAt: response.data.archivedAt,
        archivedBy: response.data.archivedBy,
        archivedByName: response.data.archivedByName,
      });
      setDisableConfirm(false);
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function enableUser() {
    let u = context.addSpiner();
    axios.post('/api/admin/physicians/updateStatus', {
      id,
      statusType: "ACTIVE",
    }).then((response) => {
      setUser({
        ...user,
        statusType: response.data.statusType,
        archivedAt: response.data.archivedAt,
        archivedBy: response.data.archivedBy,
        archivedByName: response.data.archivedByName,
      });
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateRole(newStaffRole) {
    if (newStaffRole === user.staffRole) {
      return;
    }
    let u = context.addSpiner();
    axios.post('/api/admin/physicians/updateRole', {
      id,
      staffRole: newStaffRole,
    }).then(() => {
      setUser({
        ...user,
        staffRole: newStaffRole,
      });
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function deleteUser() {
    let u = context.addSpiner();
    axios.post('/api/admin/physicians/delete', {
      id,
    }).then(() => {
      navigate('/settings/users');
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function openDeleteConfirm() {
    let u = context.addSpiner();
    axios.post('/api/admin/physicians/attachments', {
      id,
    }).then((response) => {
      setAttachments(response.data);
      setDeleteConfirm(true);
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function closeDeleteConfirm() {
    setDeleteConfirm(false);
    setAttachments(null);
  }

  function attachmentSummary() {
    if (!attachments) {
      return null;
    }

    const parts = [];
    const add = (n, singular, plural) => {
      if (n > 0) {
        parts.push(n + " " + (n === 1 ? singular : plural));
      }
    };
    add(attachments.patients, "patient", "patients");
    add(attachments.projects, "project", "projects");
    add(attachments.tasks, "task", "tasks");
    add(attachments.appointments, "appointment", "appointments");

    if (parts.length === 0) {
      return null;
    }

    let list;
    if (parts.length === 1) {
      list = parts[0];
    } else if (parts.length === 2) {
      list = parts[0] + " and " + parts[1];
    } else {
      list = parts.slice(0, -1).join(", ") + ", and " + parts[parts.length - 1];
    }

    return "This user is still assigned to " + list + ". Those records will keep the name, but the account will be removed.";
  }

  function renderUserStatus() {
    if (!user) {
      return null;
    }
    if (user.statusType === "DRAFT") {
      return <Status color={'blue'} muted={true} text={'Draft'} />;
    }
    if (user.statusType === "ACTIVE") {
      return <Status color={'green'} muted={true} text={'Active'} />;
    }
    if (user.statusType === "ARCHIVED") {
      return <Status color={'grey'} muted={true} text={'Disabled'} />;
    }
    return null;
  }

  if (loading) {
    return <></>;
  }

  const assignedSummary = attachmentSummary();

  return (
    <>
      <Helmet title="Settings | User Edit" />

      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col-xs-12 col-lg-offset-3 col-lg-6">
            <div className="d-flex justify-content-between align-items-center">
              <div className="">
                <div className="typo-hero-small">User Update</div>
                <div className="typo-normal-175">Update user information and settings</div>
              </div>
              <div className="w-25 text-end">
                {renderUserStatus()}
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-xs-12 col-lg-offset-3 col-lg-6">

            {/* Personal Information */}
            <div className="card mb-4">
              <div className="card-header sm">
                <div className="typo-strong-200">Personal Information</div>
              </div>
              
              <div className="card-header sm">
                <div className="row mb-3">
                  <div className="col-xs-12 col-lg-4">
                    <div className="typo-strong-150">First Name <span className="text-red">*</span></div>
                    <input type="text" placeholder="" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="input sm typo-normal-150 w-100" readOnly={!context.isAdmin} />
                  </div>
                  <div className="col-xs-12 col-lg-4">
                    <div className="typo-strong-150">Middle Name</div>
                    <input type="text" placeholder="" value={middleName} onChange={(e) => setMiddleName(e.target.value)} className="input sm typo-normal-150 w-100" readOnly={!context.isAdmin} />
                  </div>
                  <div className="col-xs-12 col-lg-4">
                    <div className="typo-strong-150">Last Name <span className="text-red">*</span></div>
                    <input type="text" placeholder="" value={lastName} onChange={(e) => setLastName(e.target.value)} className="input sm typo-normal-150 w-100" readOnly={!context.isAdmin} />
                  </div>
                </div>

                <div className="row">
                  <div className="col-xs-12 col-lg-4">
                    <div className="typo-strong-150">Speciality</div>
                    <input type="text" placeholder="" value={speciality} onChange={(e) => setSpeciality(e.target.value)} className="input sm typo-normal-150 w-100" readOnly={!context.isAdmin} />
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="card mb-4">
              <div className="card-header sm">
                <div className="typo-strong-200">Contact Information</div>
              </div>
              
              <div className="card-header sm">
                <div className="row">
                  <div className="col-xs-12 col-lg-4">
                    <div className="typo-strong-150">Phone</div>
                    <input type="text" placeholder="" value={phone} onChange={(e) => setPhone(e.target.value)} className="input sm typo-normal-150 w-100" readOnly={!context.isAdmin} />
                  </div>
                </div>
              </div>
            </div>

            {/* Provider Information */}
            <div className="card mb-4">
              <div className="card-header sm">
                <div className="typo-strong-200">Provider Information</div>
              </div>
              
              <div className="card-header sm">
                <div className="row">
                  <div className="col-xs-12 col-lg-4">
                    <div className="typo-strong-150">Provider <span className="text-red">*</span></div>
                    <div className={"item typo-normal-150 me-1 " + (provider ? "blue" : "grey")} onClick={() => context.isAdmin && setProvider(true)}>Yes</div>
                    <div className={"item typo-normal-150 me-1 " + (provider ? "grey" : "blue")} onClick={() => context.isAdmin && setProvider(false)}>No</div>
                  </div>
                  <div className="col-xs-12 col-lg-4">
                    <div className="typo-strong-150">NPI</div>
                    <input type="text" placeholder="" value={npi} onChange={(e) => setNpi(e.target.value)} className="input sm typo-normal-150 w-100" readOnly={!context.isAdmin} />
                  </div>
                </div>
              </div>
            </div>

            {/* Login Information */}
            <div className="card mb-4">
              <div className="card-header sm">
                <div className="typo-strong-200">Authorization Information</div>
              </div>
              
              <div className="card-header sm">
                <div className="row">
                  <div className="col-xs-12 col-lg-4">
                    <div className="typo-strong-150">Email <span className="text-red">*</span></div>
                    <input type="text" placeholder="" value={email} onChange={(e) => setEmail(e.target.value)} className="input sm typo-normal-150 w-100" readOnly={!context.isAdmin} />
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-xs-12 col-lg-8">
                    <div className="typo-strong-150">Role</div>
                    {context.isAdmin && !isProtected ? (
                      <>
                        <div className={"item typo-normal-150 me-1 " + (user.staffRole === "admin" ? "grey" : "blue")} onClick={() => updateRole("navigator")}>Navigator</div>
                        <div className={"item typo-normal-150 me-1 " + (user.staffRole === "admin" ? "blue" : "grey")} onClick={() => updateRole("admin")}>Admin</div>
                        <div className="typo-normal-150 text-muted mt-1">Saved immediately. The user is signed out and gets the new role at their next login.</div>
                      </>
                    ) : (
                      <div className="typo-normal-150">{user.staffRole === "admin" ? "Admin" : "Navigator"}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {context.isAdmin && !isProtected && (
              <div className="card">
                <div className="card-header sm">
                  <div className="typo-strong-200">Account status</div>
                </div>
                <div className="card-header sm">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="typo-normal-150 me-3">
                      {user.statusType === "ARCHIVED"
                        ? "This user is disabled and cannot log in. Enable them to restore access, or delete them permanently."
                        : "Disable this user to block login. After they are disabled, you can delete the account."}
                    </div>
                    <div className="text-end nowrap">
                      {user.statusType === "ARCHIVED" ? (
                        <>
                          <Button onClick={enableUser} color="blue" text="Enable User" className="me-2" />
                          <Button onClick={openDeleteConfirm} color="red" text="Delete User" />
                        </>
                      ) : (
                        <Button onClick={() => setDisableConfirm(true)} color="red" text="Disable User" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-xs-12 col-lg-offset-3 col-lg-6 text-end">
            {context.isAdmin && <Button onClick={updateUser} color="blue" text="Update" />}
          </div>
        </div>
      </div>

      {disableConfirm && (
        <div className="fixed-bottom modal-bg" onClick={() => setDisableConfirm(false)}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-6 col-lg-offset-3">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header sm">
                    <span className="typo-strong-300">Disable user</span><br/>
                    <span className="typo-normal-175">This user will no longer be able to log in. You can enable or delete them afterwards.</span>
                  </div>
                  <div className="card-content sm text-end">
                    <Button onClick={() => setDisableConfirm(false)} color="grey" text="Cancel" className="me-2" />
                    <Button onClick={disableUser} color="red" text="Disable User" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed-bottom modal-bg" onClick={closeDeleteConfirm}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-6 col-lg-offset-3">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header sm">
                    <span className="typo-strong-300">Delete user</span><br/>
                    <span className="typo-normal-175">This permanently deletes the user. This cannot be undone.</span>
                    {assignedSummary && (
                      <>
                        <br/>
                        <span className="typo-normal-175">{assignedSummary}</span>
                      </>
                    )}
                  </div>
                  <div className="card-content sm text-end">
                    <Button onClick={closeDeleteConfirm} color="grey" text="Cancel" className="me-2" />
                    <Button onClick={deleteUser} color="red" text="Delete User" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default User;
